import React, { useState } from 'react';
import { RaRecord, RecordContextProvider, DatagridBody, useRecordContext } from 'react-admin';
import { TableRow, TableCell, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatDateTime, dayjs } from '../ex_dayjs';

/** Grouping configuration accepted by {@link RaBodyGrouping}. */
interface GroupingConfig {
    /** True: group rows | False: un-group rows. Default: `true`. */
    isGroup?: boolean;
    /** True: expand all columns (grouped) | False: collapse all columns (grouped). Default: `true`. */
    isExpand?: boolean;
    /** Columns for grouping. Default: `[]`. */
    groupColumn?: string[];
    /** Custom columns component (rendered as-is instead of the grouped value). Default: `[]`. */
    formattedColumn?: string[];
    /** Column source used to anchor/display the group row. If not declared, uses the first column. Default: `''`. */
    anchorColumn?: string;
}

const defaultGrouping: Required<GroupingConfig> = {
    isGroup: true,
    isExpand: true,
    groupColumn: [],
    formattedColumn: [],
    /**If not declare, get the first col */
    anchorColumn: ''
};

/** A grouped datagrid record: any record plus an optional `parent` (group) reference. */
interface GroupedRecord extends RaRecord {
    parent?: unknown;
}

type GroupedField = React.ReactElement<{ source?: string; textAlign?: React.CSSProperties['textAlign'] }>;

interface MyDatagridRowProps {
    id?: RaRecord['id'];
    onToggleItem?: (id: RaRecord['id']) => void;
    children?: GroupedField[];
    grouping: GroupingConfig;
}

const MyDatagridRow: React.FC<MyDatagridRowProps> = ({ id: _id, onToggleItem: _onToggleItem, children = [], ...props }) => {
    const { isGroup, isExpand, groupColumn, formattedColumn, anchorColumn } = { ...defaultGrouping, ...props.grouping };

    const indentCellCount = children.findIndex(_ => _.props.source === anchorColumn);
    const indentCells = indentCellCount >= 0 ? children.filter((obj, index) => index < indentCellCount) : [];
    const colSpan = indentCellCount >= 0 ? children.length - indentCellCount : children.length;
    let anchorColumn2 = indentCellCount < 0 ? children[0].props.source : anchorColumn;

    const [expanded, setExpanded] = useState(isExpand);
    const handleToggleSelection = () => {
        if (!expanded)
            document.querySelectorAll(`.${'child' + setChildClass()}`).forEach(e => (e as HTMLElement).style.display = '');
        else
            document.querySelectorAll(`.${'child' + setChildClass()}`).forEach(e => (e as HTMLElement).style.display = 'none');
        setExpanded(!expanded);
    };

    const record = useRecordContext<GroupedRecord>() || {} as GroupedRecord;
    const setChildClass = () => {
        let rs = '';
        groupColumn.forEach(col => rs += record[col]);
        return rs.replace(/\s/g, '').replace(/[^\w\s]/gi, '');
    };
    const isValidDate = (dateString: string) => {
        return dayjs(dateString, 'YYYY-MM-DDTHH:mm:ss', true).isValid();// Strick check for our datetime format
    };
    const formatDateCell = (value: string) => {
        if (isValidDate(value))
            return formatDateTime(value, 'ddd MMM DD YYYY hh:mm A');
        else
            return value;
    };
    const formatCell = (value: string, index: number) => {
        let seperateStr = ' - ';
        if (index > 1)
            return seperateStr + formatDateCell(value);
        else
            return formatDateCell(value);
    };
    return (
        <RecordContextProvider value={record}>
            {record.id === 0 ? (
                <TableRow className='no-records'>
                    <TableCell colSpan={colSpan}>
                        No records found
                    </TableCell>
                </TableRow>
            ) : (
                <TableRow hover={true}
                    style={isGroup ? (!record.parent ? (!expanded ? { display: 'none' } : {}) : {}) : {}} //hide child
                    className={!record.parent ? ('child' + setChildClass()) : ''}
                >
                    {isGroup && <TableCell padding="none" >
                        {!!record.parent && <IconButton aria-label="expand row" size="small" onClick={handleToggleSelection}>
                            {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </IconButton>}
                    </TableCell>}
                    {/* fill data for child-row */}
                    {(!record.parent as any) && React.Children.map(children, field => (
                        <TableCell sx={{ textAlign: field.props.textAlign || '' }}>
                            {formattedColumn.some(col => col === field.props.source) ? (isGroup ? record[field.props.source as string] : field) : field}
                        </TableCell>
                    ))}
                    {/* format parent-row */}
                    {record.parent && isGroup && React.Children.map(children, field => {
                        if (field.props.source === anchorColumn2)
                            return (
                                <>
                                    {indentCells.map((obj, objIndex) => (<TableCell key={objIndex} />))}
                                    <TableCell colSpan={colSpan} style={{ fontWeight: 'bold' }}>
                                        {groupColumn.map((col, index) => (
                                            <div key={col} style={{ display: 'inline-block', marginLeft: index === 1 ? '12px' : '', whiteSpace: 'pre' }}>
                                                {formattedColumn.some(fcol => fcol === col) ? children.find(f => f.props.source === col) : formatCell(record[col], index)}
                                            </div>
                                        ))}
                                    </TableCell>
                                </>
                            );
                    })}
                </TableRow>
            )}

        </RecordContextProvider>
    );
};

/** Props for {@link RaBodyGrouping} — react-admin's `DatagridBody` props plus `grouping`. */
interface RaBodyGroupingProps extends Omit<React.ComponentProps<typeof DatagridBody>, 'row'> {
    /** Grouping configuration; defaults to `{ isGroup: true, isExpand: true, groupColumn: [], formattedColumn: [], anchorColumn: '' }`. */
    grouping?: GroupingConfig;
}

/**
 * `DatagridBody` variant that renders parent/child grouped rows: parent rows show the group
 * columns bolded and indented, child rows can be expanded/collapsed via {@link MyDatagridRow}.
 */
const RaBodyGrouping: React.FC<RaBodyGroupingProps> = (props) => {
    return (
        <DatagridBody {...props} row={<MyDatagridRow grouping={props.grouping || defaultGrouping} />} key={new Date().toISOString()} />
    );
};
export default React.memo(RaBodyGrouping);
